import React from 'react';
import {
  LayoutDashboard,
  Link as LinkIcon,
  Layers,
  BarChart3,
  Settings,
  ExternalLink,
  ChevronRight,
} from 'lucide-react';
import { cn } from '../lib/utils';

interface SidebarProps {
  activePath: string;
  onNavigate: (path: string) => void;
  className?: string;
}

export const Sidebar: React.FC<SidebarProps> = ({ activePath, onNavigate, className }) => {
  const navItems = [
    {
      label: 'Overview',
      path: '/admin',
      icon: LayoutDashboard,
      description: 'KPIs & click summary',
    },
    {
      label: 'Links',
      path: '/admin/links',
      icon: LinkIcon,
      description: 'Manage & create URLs',
    },
    {
      label: 'Campaigns',
      path: '/admin/campaigns',
      icon: Layers,
      description: 'UTM & campaign groups',
    },
    {
      label: 'Analytics',
      path: '/admin/analytics',
      icon: BarChart3,
      description: 'In-depth traffic stats',
    },
    {
      label: 'Settings',
      path: '/admin/settings',
      icon: Settings,
      description: 'Staff allowlist & audit',
    },
  ];

  return (
    <aside className={cn('w-64 flex flex-col justify-between border-r border-slate-800/80 bg-slate-950/60 p-4', className)}>
      <div className="space-y-6">
        <div>
          <p className="px-3 text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-2">
            Navigation
          </p>
          <nav className="space-y-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive =
                item.path === '/admin'
                  ? activePath === '/admin' || activePath === ''
                  : activePath.startsWith(item.path);

              return (
                <button
                  key={item.path}
                  onClick={() => onNavigate(item.path)}
                  className={cn(
                    'w-full flex items-center justify-between px-3.5 py-2.5 rounded-xl text-sm font-medium transition-all group select-none text-left cursor-pointer',
                    isActive
                      ? 'bg-orange-500/15 text-orange-400 font-semibold border border-orange-500/30'
                      : 'text-slate-400 hover:text-slate-100 hover:bg-slate-900/80'
                  )}
                >
                  <div className="flex items-center gap-3">
                    <Icon
                      className={cn(
                        'w-4 h-4 transition-colors',
                        isActive ? 'text-orange-400' : 'text-slate-400 group-hover:text-slate-200'
                      )}
                    />
                    <span>{item.label}</span>
                  </div>
                  <ChevronRight
                    className={cn(
                      'w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition-opacity',
                      isActive && 'opacity-100 text-orange-400'
                    )}
                  />
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Public domain shortcut */}
      <div className="pt-4 border-t border-slate-800/60">
        <a
          href="/"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-between p-3 rounded-xl bg-slate-900/40 border border-slate-800/60 text-xs text-slate-400 hover:text-slate-200 hover:border-slate-700 transition-colors group"
        >
          <div>
            <p className="font-medium text-slate-300">Public Domain</p>
            <p className="text-[10px] text-slate-500">lnk.brototype.com</p>
          </div>
          <ExternalLink className="w-3.5 h-3.5 text-slate-500 group-hover:text-slate-300 transition-colors" />
        </a>
      </div>
    </aside>
  );
};
